import { AnnouncementEnum } from "@gap/backend/app-services/announcement/enums/AnnouncementEnums"
import { AnnouncementDto } from "@gap/backend/app-services/announcement/models/AnnouncementDto"
import { apiClient } from "api/trpc/trpc-client"
import WebSocketStore from "websockets/WebSocketStore"
import { AnnouncementContent } from "./Announcements"
import AnnouncementXPMDisconnected from "./instances/AnnouncementXPMDisconnected"
import AnnouncementXPMSyncErrors from "./instances/AnnouncementXPMSyncErrors"
import AnnouncementFYISyncErrors from "./instances/AnnouncementFYISyncErrors"
import { createStoreContext } from "reusable/mobx-store-utils/storeUtils"
import AnnouncementGoose from "./instances/AnnouncementGoose"
import { reaction } from "mobx"
import OrgGuardStore from "features/main/main-router/org-guard/OrgGuardStore"
import { AllAnnouncementsEnum } from "features/main/main-router/org-guard/main-container/announcement/Announcement"
import AnnouncementOffline from "./instances/AnnouncementOffline"
import userCache from "cache/userCache"
import { UserDto } from "@gap/backend/app-services/user/models/UserDto"
import AnnouncementWorkitemDeprecation from "./instances/AnnouncementWorkitemDeprecation"

type StoreAnnouncement = AnnouncementDto & { rememberDismissed?: boolean }

const defaultAnnouncements: Record<'workitemDeprecation' | 'offline' | 'goose', StoreAnnouncement> = {
   workitemDeprecation: {
      announcement: 'workitemDeprecation',
      announcementType: 'new',
      dismissible: true,
      rememberDismissed: true,
      orgId: '',
      userId: ''
   },
   offline: {
      announcement: 'offline',
      announcementType: 'error',
      dismissible: false,
      orgId: '',
      userId: ''
   },
   goose: {
      announcement: 'goose',
      announcementType: 'info',
      dismissible: false,
      orgId: '',
      userId: ''
   }
}

export default class AnnouncementStore {
   sp
   private announcements: StoreAnnouncement[] = []

   constructor(sp: { webSocketStore: WebSocketStore, orgGuardStore: OrgGuardStore, userInfo: UserDto.info | undefined }) {
      this.sp = sp
      reaction(() => this.hasXpmConnectedAnnouncement, async () => {
         if (this.hasXpmConnectedAnnouncement) {
            // Eagerly set this in lieu of refetching the org
            this.sp.orgGuardStore.userOrg.xeroAuthorised = false
            if (this.sp.orgGuardStore.userOrg.syncVia === 'xpm') {
               this.sp.orgGuardStore.userOrg.syncVia = undefined
            }
         }
      })
      sp.webSocketStore.connectionFailureCallbacks.push({
         onFailure: this.addOfflineAnnouncement,
         onReconnect: this.removeOfflineAnnouncement
      })
   }

   get allAnnouncements(): Record<AnnouncementEnum, AnnouncementContent> {
      return this.announcements.reduce((acc, a) => {
         acc[a.announcement] = {
            content: this.getAnnouncementContent(a.announcement),
            type: a.announcementType,
            dismissible: a.dismissible
         }
         return acc
      }, {} as Record<AnnouncementEnum, AnnouncementContent>)
   }

   get hasXpmConnectedAnnouncement() {
      return !!this.announcements.find(({ announcement }) => announcement === 'xpmDisconnected')
   }

   get shouldFetchAnnouncements() {
      const isServiceUser = userCache.serviceUser.get()
      return !isServiceUser
   }

   addGoose = () => {
      if (this.addIfNotExists(defaultAnnouncements.goose)) {
         setTimeout(() => {
            this.announcements = this.announcements.filter(x => x.announcement !== 'goose')
         }, 3000)
      }
   }

   addOfflineAnnouncement = () => {
      this.addIfNotExists(defaultAnnouncements.offline)
   }

   removeWorkitemDeprecationAnnouncement = () => {
      this.announcements = this.announcements.filter(x => x.announcement !== 'workitemDeprecation')
   }

   addWorkitemDeprecationAnnouncement = () => {
      this.addIfNotExists(defaultAnnouncements.workitemDeprecation)
   }

   addIfNotExists = (announcement: StoreAnnouncement) => {
      if (announcement.rememberDismissed && this.isRememberedAsDismissed(announcement.announcement)) {
         return false
      }
      if (!this.announcements.find(x => x.announcement === announcement.announcement)) {
         this.announcements.push(announcement)
         return true
      }
      return false
   }

   isRememberedAsDismissed = (announcement: AnnouncementEnum) => {
      return (userCache.dismissedAnnouncements.get() ?? []).includes(announcement)
   }

   rememberDismissed = (announcement: AnnouncementEnum) => {
      if (!this.isRememberedAsDismissed(announcement)) {
         userCache.dismissedAnnouncements.set([...(userCache.dismissedAnnouncements.get() ?? []), announcement])
      }
   }

   removeOfflineAnnouncement = () => {
      this.announcements = this.announcements.filter(x => x.announcement !== 'offline')
   }

   getAnnouncementContent = (announcement: AnnouncementEnum): React.ReactNode | null => {
      switch (announcement) {
         case 'xpmDisconnected':
            return <AnnouncementXPMDisconnected />
         case 'xpmSyncErrors':
            return <AnnouncementXPMSyncErrors />
         case 'fyiSyncErrors':
            return <AnnouncementFYISyncErrors />
         case 'goose':
            return <AnnouncementGoose />
         case 'offline':
            return <AnnouncementOffline />
         case 'workitemDeprecation':
            return <AnnouncementWorkitemDeprecation />
         default:
            return null
      }
   }

   dismissAnnouncement = (announcementKey: AllAnnouncementsEnum) => () => {
      const announcementToDismiss = this.announcements.find(a => a.announcement === announcementKey)
      if (announcementToDismiss) {
         if (announcementToDismiss.rememberDismissed) {
            this.rememberDismissed(announcementToDismiss.announcement)
         }
         apiClient.user.dismissAnnouncement.mutate({ announcement: announcementToDismiss.announcement })
         this.announcements = this.announcements.filter(a => a.announcement !== announcementKey)
      }
   }

   fetchAnnouncements = async () => {
      if (this.shouldFetchAnnouncements) {
         const announcements = await apiClient.user.getAnnouncements.query()
         if (this.announcements.some(a => a.announcement === 'workitemDeprecation')) {
            announcements.push(defaultAnnouncements.workitemDeprecation)
         }
         this.announcements = announcements
      }
   }

   subscribeToAnnouncements = async (params: { orgId: string, userId: string }) => {
      if (this.shouldFetchAnnouncements) {
         const { orgId, userId } = params
         await this.sp.webSocketStore.addSubscriptions(
            {
               orgId,
               userId,
               subscriptions: [
                  {
                     event: 'announcement',
                     entityId: orgId,
                     callback: async () => {
                        await this.fetchAnnouncements()
                     }
                  }
               ]
            }
         )
      }
   }
}

export const { Provider: AnnouncementStoreProvider, useProvidedStore: useAnnouncementStore } = createStoreContext(AnnouncementStore)
